import Table from "components/molecules/Table";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedUniqueValues,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { MouseEventHandler, useState } from "react";
import { ArrowUpIcon, ArrowDownIcon, ArrowsUpDownIcon, ArrowUpTrayIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "react-tooltip";
import { MagnifyingGlassIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import CirclePreviewStack from "../Components/CirclePreview/CirclePreviewStack";

function MementoLegendTooltip() {
  return (
    <span>
      <InformationCircleIcon className="mementos-legend ml-1 inline-block h-5 w-5 cursor-pointer text-primary transition-transform hover:scale-110" />
      <Tooltip
        anchorSelect=".mementos-legend"
        variant="dark"
        place="bottom"
        style={{ opacity: 1, background: "#1f2732" }}
      >
        <div className="my-2 flex gap-3 font-satoshi">
          <h2 className="mx-2 font-semibold text-white">Legend for Mementos:</h2>
          <div>
            <div className="mb-3 flex gap-2">
              <div className="legend-highlited mx-auto flex h-14 w-14 cursor-pointer items-center justify-center overflow-hidden rounded-full border-[3px] border-lime-500 font-satoshi text-base font-extrabold text-primary hover:border-lime-400" />
              <div className="w-40 text-xs font-normal text-white">Acquired and User Interaction Completed</div>
            </div>
            <div className="flex gap-2">
              <div className="legend-highlited mx-auto flex h-14 w-14 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-neutral-300 font-satoshi text-base font-extrabold text-primary hover:border-white" />
              <div className="w-40 text-xs font-normal text-white">Acquired and User Interaction Pending</div>
            </div>
          </div>
        </div>
      </Tooltip>
    </span>
  );
}

interface IProps {
  data: AudienceAnalyticsData[];
  handleExport: MouseEventHandler;
}

const columnHelper = createColumnHelper<AudienceAnalyticsData>();

const columns = [
  columnHelper.accessor("username", {
    header: "Username",
    cell: (info: any) => (
      <Link to={`/user-profile/${info.getValue()}`} target="_blank" className="text-white hover:underline">
        {info.getValue()}
      </Link>
    ),
    footer: (info: any) => info.column.id,
  }),
  columnHelper.accessor("email", {
    cell: (info: any) => (
      <a href={`mailto: ${info.getValue()}`} className="text-white hover:underline">
        {info.getValue()}
      </a>
    ),
    header: "Email",
    footer: (info: any) => info.column.id,
  }),
  columnHelper.accessor("magnets_owned", {
    header: "Magnets Owned",
    footer: (info: any) => info.column.id,
    cell: (info: any) => <div className="flex justify-center pr-10">{info.getValue()}</div>,
  }),
  columnHelper.accessor("mementos_owned", {
    header: "Mementos Owned",
    footer: (info: any) => info.column.id,
    cell: (info: any) => <div className="flex justify-center pr-12">{info.getValue()}</div>,
  }),
  columnHelper.accessor((row) => row.magnets.map((e) => e.name).join(" "), {
    header: "Magnets",
    cell: (info: any) => (
      <CirclePreviewStack dataArray={info.row.original.magnets || []} previewOf="Magnet" limit={3} />
    ),
    footer: (info: any) => info.column.id,
  }),
  columnHelper.accessor((row) => row.mementos.map((e) => e.name).join(" "), {
    header: "Mementos",
    cell: (info: any) => (
      <CirclePreviewStack dataArray={info.row.original.mementos || []} previewOf="Memento" limit={3} />
    ),
    footer: (info: any) => info.column.id,
  }),
];

function AudienceTableNew({ data, handleExport }: IProps) {
  const [globalFilter, setGlobalFilter] = useState("");

  const table = useReactTable({
    data,
    columns,
    columnResizeMode: "onChange",
    state: {
      globalFilter,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <div className="flex flex-col">
      <h2 className="font-satoshi text-3xl font-bold text-white">Audience Insights</h2>
      <div className="mb-2 flex items-center justify-between">
        <div className="relative mt-2 rounded-md shadow-sm">
          <input
            type="text"
            value={globalFilter}
            placeholder="Enter username / email / Memento name / Magnet name to filter"
            className="w-[28vw] rounded-full border-1.5 border-white bg-white pl-5 pt-1.5 font-satoshi text-xs transition-all focus:w-[30vw]"
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
          <div className="pointer-events-none absolute inset-y-0 bottom-0.5 right-0 flex items-center pr-5">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
        </div>
        <button
          type="button"
          onClick={handleExport}
          title="Click to Export Entire Audience Data"
          className="mr-1.5 inline-flex items-center gap-x-1.5 rounded-md border-1 border-secondary bg-transparent px-3 py-2 text-xxs font-semibold text-secondary shadow-sm hover:bg-secondary hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <ArrowUpTrayIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
          Export Data
        </button>
      </div>
      <div className=" w-full min-w-[900px] rounded-2xl border border-secondary">
        <Table>
          <Table.Header>
            {table.getHeaderGroups().map((headerGroup: any) => (
              <Table.Row isHeaderRow key={`audience_table_rows${headerGroup.id}`}>
                {headerGroup.headers.map((header: any) => (
                  <Table.HeaderCell
                    align="left"
                    label={
                      header.isPlaceholder ? null : (
                        <button
                          type="button"
                          {...{
                            className: header.column.getCanSort() ? "cursor-pointer select-none" : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.columnDef.header === "Mementos" && <MementoLegendTooltip />}
                          {{
                            asc: <ArrowUpIcon className="ml-1 inline-block h-4 w-4" />,
                            desc: <ArrowDownIcon className="ml-1 inline-block h-4 w-4" />,
                            false: <ArrowsUpDownIcon className="ml-1 inline-block h-5 w-5" />,
                          }[header.column.getIsSorted() as string] ?? null}
                        </button>
                      )
                    }
                    key={`audience_table_header_${header.id}`}
                  />
                ))}
              </Table.Row>
            ))}
          </Table.Header>
          {table.getRowModel().rows.length === 0 ? (
            <Table.EmptyState
              onClick={() => {}}
              emptyStatefor="Brand"
              title="Create Magnet"
              emptyContent={
                <div className="rounded-lg border-1 border-dashed border-white p-5 text-white opacity-80">
                  No purchase recorded yet! You can checkout your brand&apos;s audience and their interaction with your
                  programs here.
                </div>
              }
            />
          ) : (
            <Table.Body className="font-satoshi text-sm font-semibold text-white">
              {table.getRowModel().rows.map((row) => (
                <Table.Row key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Table.Description textAlign="start" key={`audience_table_cell_${cell.id}`}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Description>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          )}
        </Table>
      </div>
    </div>
  );
}

export default AudienceTableNew;
